// 상세 미리보기의 가변 폰트 줄. 축(굵기·폭·사용자 정의 축 …)마다 슬라이더를 두고, 움직이면
// 그 줄의 글자(sample)에 font-variation-settings 를 바로 입힌다.
//
// <div data-controller="fonts--variable-axes"
//      data-fonts--variable-axes-axes-value='[{"tag":"wght","min":100,"default":400,"max":900}]'>
//   <input data-fonts--variable-axes-target="sample">
//   <input type="range" data-fonts--variable-axes-target="slider" data-tag="wght" data-action="input->fonts--variable-axes#update">
//   <span data-fonts--variable-axes-target="output" data-tag="wght"></span>
// </div>
//
// "움직여 보기" 는 모든 축을 한꺼번에 최소↔최대로 오가게 해, 가변 폰트가 무엇인지 한눈에 보이게 한다.
// 축마다 주기와 출발점을 다르게 둬서 굵기·폭·기울기 … 의 조합이 계속 바뀐다. 다시 누르거나 슬라이더를 직접 잡으면 멈춘다.
import { Controller } from "@hotwired/stimulus";

// 축마다 한 번 오가는 데 걸리는 시간. 서로 나누어떨어지지 않게 둬서 같은 조합이 금방 되풀이되지 않는다.
const SWEEP_MS = [6000, 7900, 9700, 11300, 13100];

export default class extends Controller {
  static targets = ["sample", "slider", "output", "playButton"];
  static values = { axes: Array };

  connect() {
    this.apply();
  }

  disconnect() {
    this.stop();
  }

  update() {
    this.stop();
    this.apply();
  }

  reset() {
    this.stop();
    this.sliderTargets.forEach((slider) => {
      const axis = this.axisFor(slider.dataset.tag);
      if (axis) slider.value = axis.default;
    });
    this.apply();
  }

  toggle() {
    if (this.frame) {
      this.stop();
    } else {
      this.play();
    }
  }

  play() {
    const moving = this.sliderTargets
      .map((slider) => ({ slider, axis: this.axisFor(slider.dataset.tag) }))
      .filter(({ axis }) => axis && axis.min !== axis.max)
      .map(({ slider, axis }, index) => {
        // 지금 값에서 출발하도록 곡선의 출발점을 맞춘다(누르는 순간 글자가 튀지 않게).
        const start = Math.min(Math.max((Number(slider.value) - axis.min) / (axis.max - axis.min), 0), 1);
        return { slider, axis, period: SWEEP_MS[index % SWEEP_MS.length], offset: Math.acos(1 - 2 * start) / (2 * Math.PI) };
      });
    if (moving.length === 0) return;

    const started = performance.now();
    const step = (now) => {
      moving.forEach(({ slider, axis, period, offset }) => {
        // 0 → 1 → 0 으로 오가는 코사인 곡선. 끝에서 잠깐 머물러 최소·최대 모양이 눈에 남는다.
        const phase = ((now - started) / period + offset) % 1;
        const t = (1 - Math.cos(phase * 2 * Math.PI)) / 2;
        slider.value = axis.min + (axis.max - axis.min) * t;
      });
      this.apply();
      this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
    this.playButtonTargets.forEach((button) => button.setAttribute("aria-pressed", "true"));
  }

  stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.playButtonTargets.forEach((button) => button.setAttribute("aria-pressed", "false"));
  }

  apply() {
    const settings = this.sliderTargets.map((slider) => `"${slider.dataset.tag}" ${this.format(slider.value)}`).join(", ");
    if (settings === "") return;

    this.sampleTargets.forEach((sample) => {
      sample.style.fontVariationSettings = settings;
    });
    this.outputTargets.forEach((output) => {
      const slider = this.sliderTargets.find((s) => s.dataset.tag === output.dataset.tag);
      if (slider) output.textContent = this.format(slider.value);
    });
  }

  axisFor(tag) {
    return this.axesValue.find((axis) => axis.tag === tag);
  }

  // 슬라이더 값은 소수가 섞인다. 화면에는 정수(또는 소수 한 자리)로만 적는다.
  format(value) {
    const number = Number(value);
    return Number.isInteger(number) ? String(number) : String(Math.round(number * 10) / 10);
  }
}
